import type { Ref } from 'react';
import { Search } from 'lucide-react';
import type { DataRow } from '@/lib/api';

type Props = {
  rowPickerRef: Ref<HTMLDivElement>;
  previewRow: DataRow | null;
  previewRowIdx: number;
  setPreviewRowIdx: (n: number) => void;
  rowSearch: string;
  setRowSearch: (s: string) => void;
  showRowPicker: boolean;
  setShowRowPicker: (v: boolean) => void;
  filteredCities: DataRow[];
  cityIndexMap: Map<DataRow, number>;
  rowDisplayLabel: (row: DataRow) => string;
  rowMatchHint: (row: DataRow, query: string) => string | null;
};

export function RowPicker({
  rowPickerRef, previewRow, previewRowIdx, setPreviewRowIdx,
  rowSearch, setRowSearch, showRowPicker, setShowRowPicker,
  filteredCities, cityIndexMap, rowDisplayLabel, rowMatchHint,
}: Props) {
  return (
    <div className="relative" ref={rowPickerRef}>
      <div
        className="flex items-center gap-2 border border-border/60 rounded-md px-2.5 h-8 bg-card/80 cursor-text min-w-[180px]"
        onClick={() => setShowRowPicker(true)}
      >
        <Search className="size-3 text-muted-foreground/60 shrink-0" />
        <input
          className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50 w-full"
          placeholder={previewRow ? rowDisplayLabel(previewRow) : 'Search rows…'}
          value={rowSearch}
          onChange={e => { setRowSearch(e.target.value); setShowRowPicker(true); }}
          onFocus={() => setShowRowPicker(true)}
        />
        {!rowSearch && (
          <span className="text-[10px] font-mono text-muted-foreground/40 shrink-0">#{previewRowIdx + 1}</span>
        )}
      </div>
      {showRowPicker && filteredCities.length > 0 && (
        <div className="absolute top-full mt-1 left-0 bg-popover border border-border rounded-md shadow-md z-50 max-h-52 overflow-y-auto min-w-[220px]">
          {filteredCities.map((city) => {
            const actualIdx = cityIndexMap.get(city) ?? 0;
            const hint = rowMatchHint(city, rowSearch);
            return (
              <button key={actualIdx}
                className="w-full text-left px-3 py-2 text-xs hover:bg-accent transition-colors border-b border-border/30 last:border-0"
                onClick={() => { setPreviewRowIdx(actualIdx); setRowSearch(''); setShowRowPicker(false); }}>
                <span className="font-medium">{rowDisplayLabel(city)}</span>
                {hint && <span className="ml-1.5 text-primary/70">— {hint}</span>}
                <span className="ml-2 text-[10px] text-muted-foreground/50 font-mono">#{actualIdx + 1}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
